import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';

import { CompanyMapper, PurchaseAdjustmentNoteMapper } from '@shared-classes';
import {
  DianCompany,
  DianPurchase,
  SupportDocumentAdjustmentRequest,
  XMLResponse,
} from '@shared-models';

import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { IRefundsSingle } from 'src/app/@interfaces/refunds.interface';
import { PurchasesService } from 'src/app/@pages/purchases/purchases.service';
import { RefundsService } from 'src/app/@pages/refunds/refunds.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AppState } from 'src/app/state/app.state';
import { selectCompaniesList } from 'src/app/state/selectors/user-companies.selectors';

import { DianInvoiceService } from './dian-invoice.service';
import { DianService } from './dian.service';
import { AlertService } from '../alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class DianAdjustmentNoteService {
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private store = inject(Store<AppState>);
  private spinnerService = inject(NgxSpinnerService);
  private translate = inject(TranslateService);
  private dianService = inject(DianService);
  private dianInvoiceService = inject(DianInvoiceService);
  private purchasesService = inject(PurchasesService);
  private refundsService = inject(RefundsService);
  private destroyRef = inject(DestroyRef);

  companyId = '';
  companyObject = {} as IStorageCompany;

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject.Id_company!.toString();
  }

  async sendDian(refundId: number) {
    try {
      this.getAuthValues();
      const refund = await this.getRefund(refundId);
      this.assertEligible(refund);
      const purchase = await this.getPurchase(refund.num_invo);
      this.assertOriginalDsEmitted(purchase);
      const dianCompany = await this.createDianCompany(
        purchase.electronic_support.voucher!,
        this.companyId,
      );
      const confirmed = await this.askUserConfirmation();
      if (!confirmed) throw new Error('User cancelled the operation.');
      this.spinnerService.show();
      const adjustmentNumber = this.getAdjustmentNumber(refund);
      return await this.sendRequest(
        refund,
        purchase,
        dianCompany,
        adjustmentNumber,
      );
    } catch (error) {
      this.spinnerService.hide();
      return Promise.reject(error);
    }
  }

  assertEligible(refund: IRefundsSingle) {
    // Backend Task 1.7 reserves the ESA row + sets refunds.id_adjustment_number
    // at refund-creation time, so that field being truthy only means "row
    // reserved", NOT "sent to DIAN". The actual sent-marker is the CUDS the
    // backend persists via setCudsAdjustmentNote AFTER DIAN responds — same
    // check the refunds-list button uses for visibility.
    if (refund.electronic_support_adjustment?.cuds) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.warning'),
        message: this.translate.instant('global.dian.refunds.already-sent'),
        type: 'warning',
        acceptText: 'Ok',
      });
      throw new Error('Refund already has a NAS with CUDS.');
    }
    if (!refund.concept) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.warning'),
        message: this.translate.instant('global.dian.refunds.missing-concept'),
        type: 'warning',
        acceptText: 'Ok',
      });
      throw new Error('Refund.concept (DIAN code 1-5) is required.');
    }
  }

  assertOriginalDsEmitted(purchase: DianPurchase) {
    if (!purchase?.electronic_support?.cuds) {
      this.alertService.open({
        title: this.translate.instant('global.dian.refunds.error'),
        message: this.translate.instant('global.dian.refunds.ds-not-emitted'),
        type: 'error',
        showClose: true,
      });
      throw new Error('Original DS has no CUDS.');
    }
  }

  async getPurchase(numInvo: number) {
    return new Promise<DianPurchase>((resolve) => {
      this.purchasesService
        .getPurchaseInvoice(numInvo)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((item: DianPurchase) => resolve(item));
    });
  }

  async getRefund(refundId: number) {
    return new Promise<IRefundsSingle>((resolve, reject) => {
      this.purchasesService
        .getSingleRefund(refundId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (item: IRefundsSingle) => resolve(item),
          error: (err: unknown) => reject(err),
        });
    });
  }

  async createDianCompany(voucher: number, companyId: string) {
    return new Promise<DianCompany>((resolve, reject) => {
      this.store.select(selectCompaniesList).subscribe((data) => {
        const company = data.find(
          (item) => item.Id_company.toString() === companyId,
        );
        const prefixRaw = company!.prefixes.find(
          (p) => p.voucher === voucher && p.document_type === 'DS',
        );
        if (!prefixRaw) {
          this.showErrorAlert(
            this.translate.instant('global.company.prefix.prefix-config-error'),
          );
          reject('The ds_prefix is not configured correctly.');
          return;
        }
        const prefixHasNulls = Object.values(prefixRaw).some(
          (prefix) => prefix === null || prefix === undefined,
        );
        if (prefixHasNulls) {
          this.showErrorAlert(
            this.translate.instant('global.company.prefix.prefix-config-error'),
          );
          reject('The ds_prefix is not configured correctly.');
          return;
        }
        resolve(CompanyMapper.mapper(prefixRaw, company!));
      });
    });
  }

  askUserConfirmation(): Promise<boolean> {
    return new Promise((resolve) => {
      this.alertService
        .open({
          title: this.translate.instant('global.alerts.title.warning'),
          message: this.translate.instant('global.dian.refunds.confirm-send'),
          type: 'warning',
          acceptText: this.translate.instant('Yes'),
          rejectText: this.translate.instant('Abort'),
        })
        .subscribe({
          accept: () => resolve(true),
          reject: () => resolve(false),
          close: () => resolve(false),
        });
    });
  }

  getAdjustmentNumber(refund: IRefundsSingle): string {
    const adjustment = refund?.electronic_support_adjustment;
    if (!adjustment?.document_number) {
      throw new Error(
        'Refund has no reserved electronic_support_adjustment row.',
      );
    }
    return adjustment.document_number;
  }

  async sendRequest(
    refund: IRefundsSingle,
    purchase: DianPurchase,
    dianCompany: DianCompany,
    adjustmentNumber: string,
  ) {
    return new Promise((resolve, reject) => {
      const body = PurchaseAdjustmentNoteMapper.mapper(
        refund,
        purchase as DianPurchase & {
          electronic_support: {
            document_number: string;
            cuds: string;
            issue_date?: string;
          };
        },
        adjustmentNumber,
      );
      this.dianInvoiceService
        .selectCurrency()
        .then(() => {
          const req: SupportDocumentAdjustmentRequest = {
            company: dianCompany,
            purchase_adjustment: body,
          };
          this.dianService.sendBillSyncAdjustmentNote(req).subscribe(
            async (dianResponse: XMLResponse) => {
              if (
                dianResponse.StatusCode === '00' ||
                (dianResponse.string &&
                  dianResponse.string.includes('Regla: 90'))
              ) {
                await this.onSuccessFullRequest(
                  refund,
                  adjustmentNumber,
                  dianResponse,
                );
                resolve(dianResponse);
              } else {
                this.alertService.open(
                  this.dianInvoiceService.buildAlert(dianResponse),
                );
                reject(dianResponse.StatusMessage);
              }
            },
            async (error) => {
              await this.dianInvoiceService.onUnsuccessFullRequest(error);
              reject(error);
            },
          );
        })
        .catch(async (error) => {
          await this.dianInvoiceService.onUnsuccessFullRequest(error);
          reject(error);
        });
    });
  }

  async onSuccessFullRequest(
    refund: IRefundsSingle,
    adjustmentDocumentNumber: string,
    dianResponse: XMLResponse,
  ) {
    return new Promise<unknown>((resolve, reject) => {
      this.refundsService
        .setCudsAdjustmentNote(
          refund.id_refund,
          dianResponse.XmlDocumentKey,
          adjustmentDocumentNumber,
          dianResponse.StatusCode,
        )
        .subscribe({
          next: (result) => {
            this.spinnerService.hide();
            this.alertService.open(
              this.dianInvoiceService.buildAlert(dianResponse),
            );
            resolve(result);
          },
          error: (error) => {
            this.spinnerService.hide();
            this.showErrorAlert(error);
            reject(error);
          },
        });
    });
  }

  private showErrorAlert(error: undefined | string) {
    this.alertService.open({
      title: this.translate.instant('global.dian.refunds.error'),
      message: error!,
      type: 'error',
      showClose: true,
    });
  }
}
